import React from 'react';
import { Badge, Button, Empty, List, Spin, Tabs, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useRecentAlerts } from '../../hooks/useRecentAlerts';
import { useUnreadAlerts } from '../../hooks/useUnreadAlerts';
import { notificationAlertsApi, type RecentAlert } from '../../services/alertsApi';
import { useUnreadCount } from '../../services/notificationsApi';
import { useSupportNotificationsStore } from '../../stores/useSupportNotificationsStore';
import { useFeature } from '../../features/useFeature';
import PlatformNotificationsTab from './PlatformNotificationsTab';
import SupportMessagesTab from './SupportMessagesTab';

const { Text } = Typography;

function formatTimeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMin < 1) return 'agora';
  if (diffMin < 60) return `há ${diffMin}min`;
  if (diffHours < 24) return `há ${diffHours}h`;
  if (diffDays < 7) return `há ${diffDays}d`;
  return date.toLocaleDateString('pt-BR');
}

interface NotificationDropdownProps {
  open: boolean;
  onClose: () => void;
}

/**
 * NotificationDropdown — bell content shared by Header.
 *
 * Two tabs:
 *   • "Alertas" (deals) — pre-existing UX, untouched.
 *   • "Notificações" (platform) — access requests, tenant creation,
 *     invite expirations. Backed by `notificationsApi`.
 *
 * The bell badge in Header reads (alerts unread + platform unread)
 * via `useTotalUnreadCount`.
 */
const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { alerts, isLoading } = useRecentAlerts(open);
  const { unreadCount: alertsUnread } = useUnreadAlerts();
  const { data: platformUnread = 0 } = useUnreadCount();
  // Client-side support-ticket new-message tab. Only render when the
  // `support` feature flag is on for the tenant — otherwise the tab
  // would be permanently empty and confusing.
  const { enabled: supportEnabled } = useFeature('support');
  const supportUnread = useSupportNotificationsStore((s) => s.unreadCount);
  const supportEntries = useSupportNotificationsStore((s) => s.entries);
  const [markingRead, setMarkingRead] = React.useState(false);

  const handleMarkAllAlertsRead = async () => {
    setMarkingRead(true);
    try {
      await notificationAlertsApi.markAllAsRead();
      queryClient.invalidateQueries({ queryKey: ['alerts', 'unread-count'] });
      queryClient.invalidateQueries({ queryKey: ['alerts', 'recent'] });
    } finally {
      setMarkingRead(false);
    }
  };

  const handleViewAllAlerts = () => {
    onClose();
    navigate('/alerts');
  };

  const handleAlertClick = (alert: RecentAlert) => {
    onClose();
    if (alert.dealId) {
      navigate(`/pipeline/${alert.dealId}`);
    } else {
      navigate('/alerts');
    }
  };

  const alertsPanel = (
    <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 400 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '6px 12px',
          borderBottom: '1px solid #F1F5F9',
        }}
      >
        <Button
          type="link"
          size="small"
          loading={markingRead}
          onClick={handleMarkAllAlertsRead}
          style={{ fontSize: 12, padding: 0, color: '#6366F1' }}
        >
          Marcar todos como lidos
        </Button>
      </div>
      <div style={{ overflow: 'auto', flex: 1 }}>
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
            <Spin size="small" />
          </div>
        ) : alerts.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Nenhum alerta"
            style={{ padding: '24px 0' }}
          />
        ) : (
          <List
            dataSource={alerts}
            renderItem={(alert) => (
              <List.Item
                key={alert.id}
                onClick={() => handleAlertClick(alert)}
                style={{
                  padding: '10px 16px',
                  cursor: 'pointer',
                  background: alert.read ? 'transparent' : '#F8FAFC',
                  borderBottom: '1px solid #F1F5F9',
                }}
                className="hover:bg-slate-50"
              >
                <div style={{ display: 'flex', gap: 10, width: '100%', alignItems: 'flex-start' }}>
                  {!alert.read && (
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: '#6366F1',
                        marginTop: 6,
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text strong style={{ fontSize: 13 }}>
                        {alert.companyName}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 11, flexShrink: 0, marginLeft: 8 }}>
                        {formatTimeAgo(alert.createdAt)}
                      </Text>
                    </div>
                    <Text
                      type="secondary"
                      style={{
                        fontSize: 12,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: '18px',
                      }}
                    >
                      {alert.message}
                    </Text>
                  </div>
                </div>
              </List.Item>
            )}
          />
        )}
      </div>
      <div
        style={{
          borderTop: '1px solid #F1F5F9',
          padding: '8px 16px',
          textAlign: 'center',
        }}
      >
        <Button type="link" onClick={handleViewAllAlerts} style={{ fontSize: 13, color: '#6366F1' }}>
          Ver todos
        </Button>
      </div>
    </div>
  );

  return (
    <div style={{ width: 380, display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          padding: '10px 16px',
          borderBottom: '1px solid #F1F5F9',
        }}
      >
        <Text strong style={{ fontSize: 14 }}>
          Notificações
        </Text>
      </div>
      <Tabs
        // When a brand-new support reply lands, opening the bell should
        // land the user on that tab — surfacing the most actionable news
        // first. Otherwise default to "Alertas" (legacy behaviour).
        defaultActiveKey={
          supportEnabled && supportEntries.length > 0 && supportUnread > 0 ? 'support' : 'alerts'
        }
        size="small"
        style={{ paddingLeft: 12, paddingRight: 12 }}
        items={[
          {
            key: 'alerts',
            label: (
              <Badge count={alertsUnread} size="small" offset={[6, -2]}>
                <span>Alertas</span>
              </Badge>
            ),
            children: alertsPanel,
          },
          {
            key: 'platform',
            label: (
              <Badge count={platformUnread} size="small" offset={[6, -2]}>
                <span>Notificações</span>
              </Badge>
            ),
            children: <PlatformNotificationsTab open={open} onClose={onClose} />,
          },
          // Only mount the Suporte tab when the feature is enabled — keeps
          // the dropdown tidy for tenants without support module access.
          ...(supportEnabled
            ? [
                {
                  key: 'support',
                  label: (
                    <Badge count={supportUnread} size="small" offset={[6, -2]}>
                      <span>Suporte</span>
                    </Badge>
                  ),
                  children: <SupportMessagesTab onClose={onClose} />,
                },
              ]
            : []),
        ]}
      />
    </div>
  );
};

export default NotificationDropdown;
